/**
 * @file usePermissions.js
 * @description Hook for checking user permissions in React components.
 * Imports permissions from centralized utils/permissions.js to avoid duplication.
 */
import { useCallback } from 'react';

// Re-export from centralized permissions file
export { ROLES, PERMISSIONS, PERMISSION_GROUPS, PERMISSION_LABELS, rolePermissions } from '../utils/permissions';

/**
 * Custom hook for checking user permissions.
 * @param {Object} user - The user object containing permissions array
 * @returns {Object} Object with hasPermission function
 */
const usePermissions = (user) => {
  /**
   * Check if user has a specific permission.
   * @param {string} requiredPermission - The permission to check
   * @returns {boolean} True if user has the permission
   */
  const hasPermission = useCallback((requiredPermission) => {
    if (!user || !user.permissions) {
      return false;
    }
    return user.permissions.includes(requiredPermission);
  }, [user]);

  /**
   * Check if user has any of the specified permissions.
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} True if user has at least one permission
   */
  const hasAnyPermission = useCallback((permissions) => {
    if (!user || !user.permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissions.some(p => user.permissions.includes(p));
  }, [user]);

  /**
   * Check if user has all of the specified permissions.
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean} True if user has all permissions
   */
  const hasAllPermissions = useCallback((permissions) => {
    if (!user || !user.permissions || !Array.isArray(permissions)) {
      return false;
    }
    return permissions.every(p => user.permissions.includes(p));
  }, [user]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};

export default usePermissions;
